// The recycler's fleet: eight trucks (TruckSchema) and their driver links.

import { TruckStatus, type EngineType, type TruckSchema } from '../../types/transportation.type';
import {
  DEMO_FACILITIES,
  DEMO_RECYCLER_ORG_ID,
  DEMO_TRUCKS,
  daysAgo,
  hashString,
  ids,
  iso,
  type DemoTruck,
} from '../core';
import { makeCounter, nowIso } from './common';

const LETTER_MAP: Record<string, string> = {
  A: 'أ', B: 'ب', J: 'ح', D: 'د', R: 'ر', S: 'س', X: 'ص', T: 'ط', E: 'ع', G: 'ق', K: 'ك', L: 'ل', Z: 'ز', N: 'ن', H: 'هـ', U: 'و', V: 'ى',
};
const DIGIT_MAP: Record<string, string> = { '0': '٠', '1': '١', '2': '٢', '3': '٣', '4': '٤', '5': '٥', '6': '٦', '7': '٧', '8': '٨', '9': '٩' };

export function arabicPlate(enPlate: string): string {
  const [letters = '', digits = ''] = enPlate.split(' ');
  const ar = letters.split('').map((c) => LETTER_MAP[c] ?? c).join(' ');
  const nums = digits.split('').map((d) => DIGIT_MAP[d] ?? d).join('');
  return `${ar} ${nums}`.trim();
}

const FEATURES: Record<string, string[]> = {
  'NPR 75': ['Tail lift', 'Open top'],
  Canter: ['Tail lift'],
  '500 Series': ['Hook lift', 'Open top'],
  'Atego 1218': ['Side loader', 'Tail lift'],
  FVR: ['Crane', 'Flatbed'],
  'FL Compactor': ['Compactor'],
  '300 Series': ['Tail lift', 'Open top'],
  NQR: ['Compactor', 'Tail lift'],
};

const STATUS: Record<DemoTruck['status'], TruckStatus> = {
  available: TruckStatus.AVAILABLE,
  in_transit: TruckStatus.IN_TRANSIT,
  maintenance: TruckStatus.MAINTENANCE,
  loading: TruckStatus.LOADING,
};

let trucks: TruckSchema[] = [];
const truckCounter = makeCounter(100);

function truckRecord(t: DemoTruck, i: number): TruckSchema {
  const facility = DEMO_FACILITIES.find((f) => f.id === t.facility_id) ?? DEMO_FACILITIES[0];
  const h = hashString(t.id);
  return {
    id: t.id,
    name: `${t.make} ${t.model}`,
    capacity: t.capacity_kg,
    status: STATUS[t.status],
    organization_id: DEMO_RECYCLER_ORG_ID,
    driver_id: t.driver_id,
    latitude: Number((facility.lat + ((h % 9) - 4) * 0.0006).toFixed(6)),
    longitude: Number((facility.lng + ((h % 7) - 3) * 0.0006).toFixed(6)),
    data: {
      en_plate_number: t.plate_number,
      ar_plate_number: arabicPlate(t.plate_number),
      empty_weight: Math.round(t.capacity_kg * 0.62 + 1800),
      car_model: `${t.make} ${t.model} ${t.year}`,
      capacity_unit: 'kg',
      fuel_consumption_km_per_l: Number((t.capacity_kg >= 9000 ? 3.4 : t.capacity_kg >= 5000 ? 4.6 : 6.2).toFixed(1)),
      engine_type: t.fuel_type as EngineType,
      vin: `JAL${String(t.year).slice(2)}${t.plate_number.replace(' ', '')}${String(h % 100000).padStart(5, '0')}`.slice(0, 17),
      vehicle_type: 'truck',
      price_per_trip: t.capacity_kg >= 9000 ? 650 : t.capacity_kg >= 5000 ? 450 : 320,
      truck_features: FEATURES[t.model] ?? ['Tail lift'],
    },
    created_at: iso(daysAgo(560 - i * 43)),
    updated_at: iso(daysAgo(2 + i)),
  };
}

export function resetFleet(): void {
  truckCounter.reset();
  trucks = DEMO_TRUCKS.map(truckRecord);
}

export function listTrucks(orgId: string | null | undefined): TruckSchema[] {
  return trucks.filter((t) => !orgId || t.organization_id === orgId);
}

export function truckRecordById(id: string | null | undefined): TruckSchema | undefined {
  return trucks.find((t) => t.id === id);
}

export function truckName(id: string | null | undefined): string | null {
  return truckRecordById(id)?.name ?? null;
}

export function truckForDriver(driverId: string | null | undefined): TruckSchema | undefined {
  return driverId ? trucks.find((t) => t.driver_id === driverId) : undefined;
}

export function createTruck(input: Record<string, unknown>): TruckSchema {
  const n = truckCounter.next();
  const truck: TruckSchema = {
    id: ids.truck(n),
    name: String(input.name ?? `Truck ${n}`),
    capacity: Number(input.capacity ?? 0),
    status: (input.status as TruckStatus) ?? TruckStatus.AVAILABLE,
    organization_id: String(input.organization_id ?? DEMO_RECYCLER_ORG_ID),
    driver_id: (input.driver_id as string | null | undefined) ?? null,
    latitude: DEMO_FACILITIES[0].lat,
    longitude: DEMO_FACILITIES[0].lng,
    data: (input.data as TruckSchema['data']) ?? {},
    created_at: nowIso(),
    updated_at: nowIso(),
  };
  if (truck.driver_id) unassignDriverEverywhere(truck.driver_id);
  trucks.unshift(truck);
  return truck;
}

export function updateTruck(id: string, patch: Record<string, unknown>): TruckSchema | undefined {
  const truck = truckRecordById(id);
  if (!truck) return undefined;
  if (typeof patch.name === 'string') truck.name = patch.name;
  if (patch.capacity !== undefined) truck.capacity = Number(patch.capacity);
  if (typeof patch.status === 'string') truck.status = patch.status as TruckStatus;
  if (patch.driver_id !== undefined) {
    const driverId = (patch.driver_id as string | null) || null;
    if (driverId && driverId !== truck.driver_id) unassignDriverEverywhere(driverId);
    truck.driver_id = driverId;
  }
  if (patch.data && typeof patch.data === 'object') truck.data = { ...(truck.data ?? {}), ...(patch.data as object) };
  truck.updated_at = nowIso();
  return truck;
}

export function deleteTruck(id: string): boolean {
  const before = trucks.length;
  trucks = trucks.filter((t) => t.id !== id);
  return trucks.length !== before;
}

function unassignDriverEverywhere(driverId: string): void {
  trucks.forEach((t) => {
    if (t.driver_id === driverId) {
      t.driver_id = null;
      t.updated_at = nowIso();
    }
  });
}

/** PUT /truck/{id}/assign-driver: a driver drives one truck at a time. */
export function assignDriver(truckId: string, driverId: string): TruckSchema | undefined {
  const truck = truckRecordById(truckId);
  if (!truck) return undefined;
  unassignDriverEverywhere(driverId);
  truck.driver_id = driverId;
  truck.updated_at = nowIso();
  return truck;
}

export function unassignDriver(truckId: string): TruckSchema | undefined {
  const truck = truckRecordById(truckId);
  if (!truck) return undefined;
  truck.driver_id = null;
  truck.updated_at = nowIso();
  return truck;
}

/** Move a truck marker (the live run's truck follows its route). */
export function setTruckPosition(truckId: string, lat: number, lng: number): void {
  const truck = truckRecordById(truckId);
  if (!truck) return;
  truck.latitude = Number(lat.toFixed(6));
  truck.longitude = Number(lng.toFixed(6));
}
